import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import MTopBar from "@/components/ui/atoms/MTopBar";
import MHeader from "@/components/ui/organisms/MHeader";
import headerStyles from "@/components/ui/organisms/MHeader.module.css";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { MButton } from "@/components/ui/atoms/MButtons";
import { getMemberInfo } from "@/hooks/useMembers";
import { useReminders } from "@/hooks/useReminders";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import {
  CreateReminder,
  DayFrequency,
  ReminderFrequency,
  Reminders,
  ReminderType,
  UpdateReminder,
} from "@/types/reminders";
import { VitalsParameter } from "@/types/vitals";
import {
  getPossessiveName,
  paintDayFrequencyField,
  paintDropDownFrequencySelectField,
  ReminderInputStage,
} from "../remindersHelper";
import {
  VITALS_PARAM_CONFIG,
  getVitalsFrequencyText,
  getVitalsReminderParameters,
  paintDaysBetweenTrackingField,
} from "./vitalsRemindersHelper";
import ReminderSummaryScreen from "../ReminderSummaryScreen";
import { calculateOptimalDosages } from "@/utils/dosage-scheduler";
import { DEFAULT_TIME_12HR } from "@/utils/constants";
import { getTimezone, isTimeBefore, updateTime } from "@/utils/date-utils";
import { getErrorMessage } from "@/utils/error";
import styles from "./VitalsReminder.module.css";
import summaryStyles from "../ReminderSummary.module.css";

const TRACKED_FREQUENCIES = [
  ReminderFrequency.EVERY_DAY,
  ReminderFrequency.EVERY_OTHER_DAY,
  ReminderFrequency.DAY_FREQUENCY,
];

const VitalsReminder: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const memberInfo = getMemberInfo();
  const { addReminder, updateReminder } = useReminders(memberInfo.id);

  // Editing an existing reminder (from RemindersListing's 3-dot "Edit"
  // action, which navigates here with the full reminder in state — same
  // mechanism medication's own edit mode already uses).
  const existingReminder = (location.state as { reminder?: Reminders } | null)
    ?.reminder;
  const isEditing = !!existingReminder;

  const [stage, setStage] = useState<ReminderInputStage>(
    isEditing ? ReminderInputStage.ADD : ReminderInputStage.START
  );
  // No default selection on the type-picker stage for a new reminder — the
  // user must actively pick a vital. Editing still opens with the existing
  // reminder's own parameter pre-selected.
  const [vitalParameter, setVitalParameter] = useState<VitalsParameter | null>(
    existingReminder?.vitalParameter ?? null
  );
  const [frequency, setFrequency] = useState<ReminderFrequency | null>(
    existingReminder?.frequency ?? null
  );
  const [frequencyValue, setFrequencyValue] = useState<number | null>(
    existingReminder?.frequencyValue ?? null
  );
  const [dayFrequency, setDayFrequency] = useState<DayFrequency | null>(
    existingReminder?.dayFrequency ?? null
  );
  const [showFrequencyDrawer, setShowFrequencyDrawer] = useState(false);
  const [showDayFrequencyDrawer, setShowDayFrequencyDrawer] = useState(false);

  const [reminderDate, setReminderDate] = useState<Date | null>(
    existingReminder?.startDate
      ? new Date(existingReminder.startDate)
      : new Date()
  );
  const [reminderEndDate, setReminderEndDate] = useState<Date | null>(
    existingReminder?.endDate ? new Date(existingReminder.endDate) : null
  );
  const [notes, setNotes] = useState<string | null>(
    existingReminder?.notes ?? null
  );
  const [firstDosage, setFirstDosage] = useState<string | null>(
    existingReminder?.dayFrequencyValue?.[0] ?? DEFAULT_TIME_12HR
  );
  const [secondDosage, setSecondDosage] = useState<string | null>(
    existingReminder?.dayFrequencyValue?.[1] ?? null
  );
  const [thirdDosage, setThirdDosage] = useState<string | null>(
    existingReminder?.dayFrequencyValue?.[2] ?? null
  );
  const [fourthDosage, setFourthDosage] = useState<string | null>(
    existingReminder?.dayFrequencyValue?.[3] ?? null
  );

  const memberName = getPossessiveName(
    memberInfo.nickname?.trim() || memberInfo.firstName?.trim()
  );
  const paramLabel = vitalParameter
    ? t(VITALS_PARAM_CONFIG[vitalParameter].labelKey)
    : "";
  const ParamIcon = vitalParameter
    ? VITALS_PARAM_CONFIG[vitalParameter].Icon
    : null;

  // Weight/Height only ever track once a day, every N days — no "how often
  // is it tracked" or "how many times a day" choice to make, just the days-
  // between-tracking stepper (Figma: node 5257-60910).
  const isSimpleTrackingParam =
    vitalParameter === VitalsParameter.WEIGHT ||
    vitalParameter === VitalsParameter.HEIGHT;

  useEffect(() => {
    if (isSimpleTrackingParam) {
      setFrequency(ReminderFrequency.DAY_FREQUENCY);
      setDayFrequency(DayFrequency.ONCE);
      setFrequencyValue((prev) => prev ?? 45);
    }
  }, [isSimpleTrackingParam]);

  useEffect(() => {
    if (
      firstDosage &&
      stage === ReminderInputStage.ADD &&
      (!secondDosage ||
        (dayFrequency === DayFrequency.THRICE && !thirdDosage) ||
        (dayFrequency === DayFrequency.FOUR_TIMES &&
          (!thirdDosage || !fourthDosage)))
    ) {
      const dosages = calculateOptimalDosages(dayFrequency);

      // Fixed clock times (9/1/5/9), not computed relative to firstDosage —
      // if the user picked an unusually late first dose, only accept a
      // default that keeps chronological order against the previous slot;
      // otherwise leave it blank rather than silently saving an out-of-
      // order schedule (see the identical guard in medication/index.tsx).
      let previousSlot = firstDosage;

      let effectiveSecond = secondDosage;
      if (
        !secondDosage &&
        dosages.secondDosage &&
        !isTimeBefore(dosages.secondDosage, previousSlot)
      ) {
        setSecondDosage(dosages.secondDosage);
        effectiveSecond = dosages.secondDosage;
      }
      previousSlot = effectiveSecond ?? previousSlot;

      let effectiveThird = thirdDosage;
      if (
        !thirdDosage &&
        dosages.thirdDosage &&
        effectiveSecond &&
        !isTimeBefore(dosages.thirdDosage, previousSlot)
      ) {
        setThirdDosage(dosages.thirdDosage);
        effectiveThird = dosages.thirdDosage;
      }
      previousSlot = effectiveThird ?? previousSlot;

      if (
        !fourthDosage &&
        dosages.fourthDosage &&
        effectiveThird &&
        !isTimeBefore(dosages.fourthDosage, previousSlot)
      ) {
        setFourthDosage(dosages.fourthDosage);
      }
    }
  }, [
    firstDosage,
    stage,
    dayFrequency,
    secondDosage,
    thirdDosage,
    fourthDosage,
  ]);

  const handleBack = () => {
    const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
    // Editing starts directly at ADD (type/details are skipped entirely —
    // they were already answered when the reminder was first created), so
    // there's no earlier stage to step back through here.
    if (!isEditing && stage === ReminderInputStage.FREQUENCY_DETAILS) {
      setStage(ReminderInputStage.START);
      return;
    }
    if (!isEditing && stage === ReminderInputStage.ADD) {
      setStage(ReminderInputStage.FREQUENCY_DETAILS);
      return;
    }
    if (returnTo) {
      navigate(returnTo);
      return;
    }
    navigate("/reminders");
  };

  const isTypeInvalid = (): boolean => !vitalParameter;

  const isDetailsInvalid = (): boolean => {
    if (!frequency || !dayFrequency) return true;
    if (
      frequency === ReminderFrequency.DAY_FREQUENCY &&
      (!frequencyValue || frequencyValue < 1)
    )
      return true;
    return false;
  };

  const isSummaryInvalid = (): boolean => {
    if (!reminderDate) return true;
    switch (dayFrequency) {
      case DayFrequency.ONCE:
        return !firstDosage;
      case DayFrequency.TWICE:
        return !firstDosage || !secondDosage;
      case DayFrequency.THRICE:
        return !firstDosage || !secondDosage || !thirdDosage;
      case DayFrequency.FOUR_TIMES:
        return !firstDosage || !secondDosage || !thirdDosage || !fourthDosage;
      default:
        return true;
    }
  };

  const handleNext = () => {
    if (stage === ReminderInputStage.START && !isTypeInvalid()) {
      setStage(ReminderInputStage.FREQUENCY_DETAILS);
    } else if (
      stage === ReminderInputStage.FREQUENCY_DETAILS &&
      !isDetailsInvalid()
    ) {
      setStage(ReminderInputStage.ADD);
    }
  };

  const handleCreateReminder = async () => {
    const firstDosageValue = firstDosage || DEFAULT_TIME_12HR;
    const startDate = updateTime(reminderDate, firstDosageValue);
    const endDate = reminderEndDate
      ? updateTime(reminderEndDate, firstDosageValue)
      : null;

    const dayFrequencyValue = [firstDosageValue];
    if (dayFrequency === DayFrequency.TWICE) {
      dayFrequencyValue.push(secondDosage);
    } else if (dayFrequency === DayFrequency.THRICE) {
      dayFrequencyValue.push(secondDosage, thirdDosage);
    } else if (dayFrequency === DayFrequency.FOUR_TIMES) {
      dayFrequencyValue.push(secondDosage, thirdDosage, fourthDosage);
    }

    const reminderData = {
      type: ReminderType.VITALS,
      vitalParameter,
      reminderFor: paramLabel,
      startDate,
      endDate,
      frequency,
      dayFrequency,
      dayFrequencyValue,
      notes,
      timezone: getTimezone(),
      ...(frequency === ReminderFrequency.DAY_FREQUENCY && { frequencyValue }),
    };

    try {
      if (isEditing && existingReminder) {
        await updateReminder(
          existingReminder.id,
          reminderData as UpdateReminder
        );
      } else {
        await addReminder(reminderData as CreateReminder);
      }
      const returnTo = (location.state as { returnTo?: string } | null)
        ?.returnTo;
      navigate(returnTo || "/reminders");
    } catch (err) {
      showToast({
        message: getErrorMessage(err, t("errors.toast")),
        variant: "error",
      });
    }
  };

  // Switching parameters on the type screen (e.g. Weight -> back -> Blood
  // Pressure) must clear whatever the PREVIOUS parameter's details screen
  // set — otherwise leftover frequency/dayFrequency/dosages from a simple-
  // tracking param (Weight/Height) leak into a different param's screen
  // (e.g. Blood Pressure showing the "days between tracking" stepper and a
  // pre-filled "Every 45 days" left over from Weight).
  const handleSelectVitalParameter = (param: VitalsParameter) => {
    if (param === vitalParameter) return;
    setVitalParameter(param);
    setFrequency(null);
    setFrequencyValue(null);
    setDayFrequency(null);
    // Back to the default first-dose time (not null) — matches this state's
    // own initial value above. Leaving this null broke the "always defaults
    // to 9am" contract for anyone who ever picks a non-default vital
    // parameter (i.e. almost everyone, since BP is only the initial value
    // before any selection): the second/third/fourth auto-fill effect
    // requires firstDosage to be truthy to run at all, so a null here meant
    // 2x/3x/4x a day never got its default times pre-filled on the summary
    // screen.
    setFirstDosage(DEFAULT_TIME_12HR);
    setSecondDosage(null);
    setThirdDosage(null);
    setFourthDosage(null);
    setNotes(null);
    setReminderEndDate(null);
  };

  const paintTypeStage = () => (
    <div className="relative w-full h-full flex flex-col">
      <MHeader
        module="reminders"
        memberInfo={memberInfo}
        title={`${memberName} ${t("reminders.vitals.typeTitle")}`}
        description={t("reminders.vitals.typePrompt")}
        useParentGradient={false}
        className={headerStyles.stickyBelowTopBar}
      />
      <MSpacer />
      <div className="profile-input-container flex-1 flex flex-col gap-3">
        {getVitalsReminderParameters(memberInfo.derivedMemberType).map(
          (param) => {
            const cfg = VITALS_PARAM_CONFIG[param];
            const selected = vitalParameter === param;
            return (
              <button
                key={param}
                type="button"
                className={`${styles.typeCard} ${
                  selected ? styles.typeCardSelected : ""
                }`}
                onClick={() => handleSelectVitalParameter(param)}
              >
                <div className={`${styles.typeCardIcon} ${cfg.iconClass}`}>
                  <cfg.Icon size={24} color="#45168C" />
                </div>
                <span className={styles.typeCardLabel}>{t(cfg.labelKey)}</span>
              </button>
            );
          }
        )}
      </div>
      <div className="confirm-footer pt-0">
        <MButton
          variant="filled"
          size="lg"
          onClick={handleNext}
          className="w-full"
          disabled={isTypeInvalid()}
        >
          {t("common.next")}
        </MButton>
      </div>
    </div>
  );

  const paintDetailsStage = () => (
    <div className="relative w-full h-full flex flex-col">
      <MHeader
        module="reminders"
        memberInfo={memberInfo}
        title={`${memberName} ${paramLabel.toLowerCase()} reminder`}
        description={t(
          isSimpleTrackingParam
            ? "reminders.vitals.detailsPromptSimple"
            : "reminders.vitals.detailsPrompt"
        )}
        useParentGradient={false}
        className={headerStyles.stickyBelowTopBar}
      />
      <MSpacer />
      <div className="profile-input-container flex-1 flex flex-col gap-6">
        {isSimpleTrackingParam ? (
          paintDaysBetweenTrackingField(frequencyValue, setFrequencyValue, t)
        ) : (
          <>
            {paintDropDownFrequencySelectField(
              frequency,
              setFrequency,
              showFrequencyDrawer,
              setShowFrequencyDrawer,
              t,
              "vitals",
              TRACKED_FREQUENCIES,
              t("reminders.vitals.frequencyDrawerTitle")
            )}
            {frequency === ReminderFrequency.DAY_FREQUENCY &&
              paintDaysBetweenTrackingField(
                frequencyValue,
                setFrequencyValue,
                t
              )}
            {paintDayFrequencyField(
              dayFrequency,
              setDayFrequency,
              showDayFrequencyDrawer,
              setShowDayFrequencyDrawer,
              t,
              "vitals",
              t("reminders.vitals.dayFrequencyDrawerTitle")
            )}
          </>
        )}
      </div>
      <div className="confirm-footer pt-0">
        <MButton
          variant="filled"
          size="lg"
          onClick={handleNext}
          className="w-full"
          disabled={isDetailsInvalid()}
        >
          {t("common.next")}
        </MButton>
      </div>
    </div>
  );

  const dosageBySlot: {
    value: string | null;
    onChange: (v: string) => void;
    label: string;
  }[] = [
    { value: firstDosage, onChange: setFirstDosage, label: "1" },
    { value: secondDosage, onChange: setSecondDosage, label: "2" },
    { value: thirdDosage, onChange: setThirdDosage, label: "3" },
    { value: fourthDosage, onChange: setFourthDosage, label: "4" },
  ];

  const slotCount =
    dayFrequency === DayFrequency.TWICE
      ? 2
      : dayFrequency === DayFrequency.THRICE
      ? 3
      : dayFrequency === DayFrequency.FOUR_TIMES
      ? 4
      : 1;

  const isFinalStage = stage === ReminderInputStage.ADD;

  return (
    <div
      className={`flex flex-col min-h-screen relative pb-[24px] ${
        isFinalStage ? summaryStyles.summaryPageBackground : ""
      }`}
    >
      <div className="sticky top-0 z-[31]">
        <MTopBar
          hideProfilePicker
          exitPath="/reminders"
          hideExit={stage === ReminderInputStage.START}
          onBack={handleBack}
          // Matches medication reminder's own final-stage pattern exactly:
          // showPurpleGradient (not withBackground) on the final stage, so
          // this top bar's gradient lines up with summaryPageBackground's
          // circle and ReminderSummaryScreen's stickyBelowTopBarWithGradient
          // header instead of sitting as an opaque white/mismatched strip.
          withBackground={!isFinalStage}
          showPurpleGradient={isFinalStage}
        />
      </div>
      <div
        className="profile-page-root flex flex-col"
        style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
      >
        <div className="flex-1 flex flex-col">
          {stage === ReminderInputStage.START && paintTypeStage()}
          {stage === ReminderInputStage.FREQUENCY_DETAILS &&
            paintDetailsStage()}
          {stage === ReminderInputStage.ADD && (
            <ReminderSummaryScreen
              memberInfo={memberInfo}
              title={`${
                isEditing ? t("reminders.vitals.editTitlePrefix") : ""
              }${memberName} ${paramLabel.toLowerCase()} reminder`}
              description={t(
                isEditing
                  ? "reminders.vitals.editPrompt"
                  : "reminders.vitals.summaryPrompt"
              )}
              labelKey="vitals"
              recapIcon={
                ParamIcon ? <ParamIcon size={24} color="#45168C" /> : null
              }
              recapIconClass={VITALS_PARAM_CONFIG[vitalParameter!].iconClass}
              recapLabel={paramLabel}
              recapSecondaryText={getVitalsFrequencyText(
                frequency,
                frequencyValue,
                dayFrequency,
                t,
                isSimpleTrackingParam
              )}
              reminderDate={reminderDate}
              setReminderDate={setReminderDate}
              reminderEndDate={reminderEndDate}
              setReminderEndDate={setReminderEndDate}
              timeSlots={dosageBySlot.slice(0, slotCount)}
              notes={notes}
              setNotes={setNotes}
              isInvalid={isSummaryInvalid()}
              onSubmit={handleCreateReminder}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default VitalsReminder;
